// src/utils/formLayout.js
//
// Form layouts (docs/plans/form-layout.md; keepr-api docs/SCHEMA.md § Form
// layouts): the order and groups the item FORM and the item READ VIEW render
// a card's elements in, stored as cardLayouts kind 'form' at the card tier and
// inherited down the card hierarchy (the API resolves the nearest card in the
// chain; `resolved.card_id` names it).
//
//   body = { groups: [{ id, title, description?, collapsed?, collapsedInForm?,
//                        collapsedInView?, elements: [entry] }],
//            leftOut?: [name] }
//   entry = 'name' | { element: 'name', span?: 1-12 | 'auto', newRow?: boolean }
//
// An ENTRY is the bare name (auto width, no new row — every layout stored
// before the grid) or an object that carries a width and a new-row flag (the
// form layout grid, KPE-023). Every reader goes through `entryOf`, the one
// reading the API's validator shares (utils/formGrid/limits, vendored).
//
// Where a group STARTS is per SURFACE: `collapsed: true` starts it folded on
// both the form and the read view (the original key, so a row stored before
// the split reads as it did), `collapsedInForm` / `collapsedInView` on that
// one only. The API stores one spelling per meaning (collapsed when both,
// one per-surface key when one); `groupCollapsed` is the one reading. The
// editor calls the same fact "Start expanded" (its inverse; a body with none
// of the keys starts expanded). Every group folds whatever it says here —
// these keys decide only the start, and a person's own fold is never kept.
//
// Everything here is pure: the renderers (ItemForm, ItemReadView, CardHome)
// and the editor (FormLayoutModal) share one reading of a body, so the editor's
// preview is the form an item will actually get.
//
// LEFT OUT (KPR-1211): a child card's layout may list in `leftOut` elements it
// inherits and does not use (a Trailer extending Vehicle has no odometer). The
// API admits only an inherited element the save can never demand; the
// renderer still never hides one that must stay (`mayLeaveOut`, re-asked here
// whatever is stored), so a later "required" on the parent brings it back.
// The form draws no left-out field; the read view draws one only while the
// record holds a value for it; either way it goes in a trailing "Left out of
// the form" panel, never "Other fields" (`leftOutNames`, `formLayoutPanels`).
//
// Two rules the renderer never breaks:
//   - an element the layout does not place is NEVER lost — it falls into a
//     trailing "Other fields" panel, in definition order, so an element a
//     parent or an element set gains after the layout was saved still shows
//     (unless the layout leaves it out — and a left-out value still shows);
//   - a name the card no longer has is IGNORED, never an error: a stored
//     layout goes stale when an element is removed, and the next save of the
//     layout drops the name (the editor normalizes before it saves).

import { rangeEndFor } from './dateBounds';
import { entryOf, makeEntry, spanVerdict } from './formGrid/limits';
import { isRepeatingGroup } from '../config/repeatingGroups';

export const OTHER_FIELDS_KEY = '__other__';
export const OTHER_FIELDS_LABEL = 'Other fields';
export const LEFT_OUT_KEY = '__left_out__';
export const LEFT_OUT_LABEL = 'Left out of the form';

const GROUP_ID = /^[A-Za-z0-9_-]{1,40}$/;
export const MAX_FORM_GROUPS = 50;
export const MAX_GROUP_TITLE = 80;
export const MAX_GROUP_DESCRIPTION = 500;

/** An element no layout says anything about: its type's Auto width, no new row. */
export const AUTO_PLACEMENT = Object.freeze({ span: 'auto', newRow: false });

const groupsOf = body => (Array.isArray(body?.groups) ? body.groups : []);
const entriesOf = group => (Array.isArray(group?.elements) ? group.elements : []);

/**
 * The element names a group places, in its order. A group's `elements` are
 * ENTRIES (a name, or `{ element, span?, newRow? }`); anything that wants the
 * names — a row per element, a membership test — reads them here and never
 * treats an entry as a string.
 */
export function groupNames(group) {
	return entriesOf(group).map(entry => entryOf(entry).element).filter(Boolean);
}

/**
 * What the layout asks of `name`: `{ span, newRow }` as stored (`span` 'auto'
 * when none), or null when the body does not place it. The first placement
 * wins, as it does for the renderer.
 */
export function placementOf(body, name) {
	for (const group of groupsOf(body)) {
		for (const raw of entriesOf(group)) {
			const entry = entryOf(raw);
			if (entry.element === name) return { span: entry.span, newRow: entry.newRow };
		}
	}
	return null;
}

export const FORM_SURFACE = 'form';
export const VIEW_SURFACE = 'view';

/**
 * Whether `group` starts collapsed on each surface: `{ form, view }`. A
 * per-surface key that is SET (true or false) wins for its surface — the
 * editor's draft carries both explicitly — and otherwise the `collapsed`
 * shorthand speaks for both.
 */
export function groupCollapsed(group) {
	const both = group?.collapsed === true;
	const pick = key => (typeof group?.[key] === 'boolean' ? group[key] : both);
	return { form: pick('collapsedInForm'), view: pick('collapsedInView') };
}

/**
 * The stored spelling of `{ form, view }` — `collapsed: true` for both, one
 * per-surface key for one, nothing for neither (the API's normalization) —
 * as a patch that also clears the keys it does not use.
 */
export function collapsedFields({ form = false, view = false } = {}) {
	if (form && view) return { collapsed: true };
	if (form) return { collapsedInForm: true };
	if (view) return { collapsedInView: true };
	return {};
}

const withoutCollapsed = ({ collapsed, collapsedInForm, collapsedInView, ...rest }) => rest;

/** The names `body` lists as left out, trimmed, as stored (KPR-1211). */
export function leftOutList(body) {
	const list = Array.isArray(body?.leftOut) ? body.leftOut : [];
	return list.filter(name => typeof name === 'string').map(name => name.trim()).filter(Boolean);
}

/**
 * Why `element` must stay on the form, or null when a layout may leave it out
 * (KPR-1211): it is required, required when another element holds a value,
 * or a repeating group that needs an entry — anything the save can demand. A
 * worked-out element (a formula, a driven value, a sequence) is never asked of
 * a person, so it may go whatever it says. keepr-api cardLayouts/validate
 * mustStayOnForm is the same rule; the API refuses to store the rest.
 */
export function mustStayOnForm(element) {
	const options = element?.options || element || {};
	if (options.drivenFrom || options.sequence) return null;
	if (options.required === true) return 'required';
	if (Array.isArray(options.requiredWhen) && options.requiredWhen.length) return 'requiredWhen';
	if (isRepeatingGroup(element) && Number.isInteger(options.minEntries) && options.minEntries > 0) return 'minEntries';
	return null;
}

export const mayLeaveOut = element => Boolean(element) && !mustStayOnForm(element);

/**
 * The names the layout leaves out that the renderer honours: listed in
 * `body.leftOut`, not placed in a group (a placement wins — showing a field is
 * never the wrong side), an element of the card (`elements`, the merged
 * chain) and one that may be left out NOW. A required element is never in it,
 * whatever the stored list says.
 */
export function leftOutNames(body, elements = []) {
	const listed = leftOutList(body);
	if (!listed.length) return new Set();
	const placed = new Set(groupsOf(body).flatMap(groupNames));
	const byName = new Map((elements || []).filter(el => el?.name).map(el => [el.name, el]));
	return new Set(listed.filter(name => !placed.has(name) && mayLeaveOut(byName.get(name))));
}

/** True when `body` is a form layout with at least one group. */
export function hasFormLayout(body) {
	return groupsOf(body).length > 0;
}

/**
 * The panels a form layout renders `entries` into, or null when there is no
 * layout (the caller then groups automatically — groupElementsIntoPanels).
 *
 * `entries` are the caller's own records in definition order (range slots,
 * `{ element, def }` pairs, plain elements); `getElement` pulls the element
 * out of one. `surface` is which face is rendering — 'form' (ItemForm) or
 * 'view' (ItemReadView) — and decides `collapsed`, since a group may start
 * folded on one and open on the other. Returns
 * [{ key, label, description, collapsed, entries, placements }]
 * (`placements[i]` is `{ span, newRow }` for `entries[i]`: what the layout
 * asked for, not yet held to the type's limits — formGridCells does that):
 * one panel per group that holds at least one present element, in the
 * layout's order and its elements in the layout's order, then — only when
 * something is left over — the trailing "Other fields" panel.
 *
 * An empty group renders nothing: a titled box with no fields in it reads as
 * a broken form, and the editor is where an author sees it.
 *
 * An entry the layout LEAVES OUT (`leftOutNames`) never lands in "Other
 * fields": it goes in a last panel, **Left out of the form**, after it. Which
 * left-out entries to draw at all is the caller's: the form passes none (but
 * one holding an error), the read view only those holding a value, the card
 * home all of them.
 */
export function formLayoutPanels(entries = [], body = null, { getElement = entry => entry, surface = FORM_SURFACE } = {}) {
	const groups = groupsOf(body);
	if (!groups.length) return null;
	const leftOut = leftOutNames(body, (entries || []).map(getElement));

	const placement = new Map(); // name -> { group, index, span, newRow }
	groups.forEach((group, groupIndex) => {
		(Array.isArray(group?.elements) ? group.elements : []).forEach((raw, index) => {
			const { element: name, span, newRow } = entryOf(raw);
			// First placement wins: the API refuses a duplicate, so a second
			// one can only be a hand-edited row, and showing a field twice is
			// worse than showing it once.
			if (name && !placement.has(name)) placement.set(name, { group: groupIndex, index, span, newRow });
		});
	});

	const panels = groups.map((group, groupIndex) => ({
		key: `group:${group?.id || groupIndex}`,
		label: (group?.title || '').trim() || null,
		description: (group?.description || '').trim() || null,
		collapsed: groupCollapsed(group)[surface === VIEW_SURFACE ? 'view' : 'form'],
		placed: [],
	}));
	const other = [];
	const left = [];

	(entries || []).forEach(entry => {
		const name = getElement(entry)?.name;
		if (!name) return;
		const at = placement.get(name);
		if (at) panels[at.group].placed.push({ entry, index: at.index, span: at.span, newRow: at.newRow });
		else if (leftOut.has(name)) left.push(entry);
		else other.push(entry);
	});

	const out = panels
		.filter(panel => panel.placed.length)
		.map(({ placed, ...panel }) => {
			const ordered = placed.slice().sort((a, b) => a.index - b.index);
			return {
				...panel,
				entries: ordered.map(slot => slot.entry),
				placements: ordered.map(({ span, newRow }) => ({ span, newRow })),
			};
		});
	if (other.length) {
		out.push({
			key: OTHER_FIELDS_KEY,
			label: OTHER_FIELDS_LABEL,
			description: null,
			collapsed: false,
			entries: other,
			placements: other.map(() => AUTO_PLACEMENT),
		});
	}
	if (left.length) {
		out.push({
			key: LEFT_OUT_KEY,
			label: LEFT_OUT_LABEL,
			description: null,
			collapsed: false,
			entries: left,
			placements: left.map(() => AUTO_PLACEMENT),
		});
	}
	return out;
}

/**
 * `body` with the END of every date range it does not place put right after
 * its START (KPR-626, the owner's k02): the form draws a range as two fields,
 * and an end a layout never named (one stored by an API client, or an end
 * added after the layout was saved) belongs beside its start, not in "Other
 * fields". The inserted entry is the bare name — auto width, no new row.
 *
 * `elements` are the card's merged elements (the chain), which decide what
 * pairs (utils/dateBounds `rangeEndFor`: same type, single-valued, no
 * chains). An end the body already places keeps its place, and a start the
 * body does not place pulls nothing in. Returns `body` itself when nothing
 * moves, so a caller comparing bodies sees no change.
 */
export function placeRangeEnds(body, elements = []) {
	const groups = groupsOf(body);
	if (!groups.length) return body;
	const defs = (elements || []).filter(el => el?.name);
	const byName = new Map(defs.map(el => [el.name, el]));
	// A left-out end stays left out (KPR-1211): it is not pulled in beside its start.
	const placed = new Set([...groups.flatMap(groupNames), ...leftOutList(body)]);
	let changed = false;
	const nextGroups = groups.map(group => {
		const entries = entriesOf(group);
		const out = [];
		entries.forEach(raw => {
			out.push(raw);
			const start = byName.get(entryOf(raw).element);
			const end = start ? rangeEndFor(start, defs) : null;
			if (!end || placed.has(end.name)) return;
			placed.add(end.name);
			out.push(end.name);
			changed = true;
		});
		return out.length === entries.length ? group : { ...group, elements: out };
	});
	return changed ? { ...body, groups: nextGroups } : body;
}

/** A group id that is free in `groups`, derived from `title` when it can be. */
export function newGroupId(title = '', groups = []) {
	const taken = new Set(groupsOf({ groups }).map(group => group?.id));
	const base = (title || 'group').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 32) || 'group';
	if (!taken.has(base)) return base;
	for (let n = 2; n < 10000; n++) {
		const id = `${base}-${n}`;
		if (!taken.has(id)) return id;
	}
	return `${base}-${Date.now()}`;
}

/**
 * The layout the AUTOMATIC grouping amounts to, written down — what the
 * editor starts from when a card has no layout anywhere, so "arrange the
 * form" begins from what is on screen rather than from an empty page.
 * `panels` is groupElementsIntoPanels' answer over plain elements.
 */
export function formLayoutFromPanels(panels = [], defaultLabel = 'Details') {
	const groups = [];
	(panels || []).forEach(panel => {
		const title = (panel?.label || '').trim() || defaultLabel || 'Details';
		groups.push({
			id: newGroupId(title, groups),
			title,
			// Plain elements, or the `{ element, … }` entries the editor groups.
			elements: (panel?.entries || []).map(entry => entry?.element?.name || entry?.name).filter(Boolean),
		});
	});
	return { groups };
}

/**
 * A body the API will accept, from whatever the editor holds: names the card
 * does not have (`names`, the merged element names) dropped, a name placed
 * twice kept once, titles trimmed, blank descriptions left out, and the
 * collapsed flags in their one stored spelling (collapsedFields) — the API's
 * own normalization, so comparing the editor's body to the stored one says
 * whether anything really changed.
 */
export function normalizeFormLayoutBody(body, names = null) {
	const known = names ? new Set(names) : null;
	const seen = new Set();
	const out = {
		groups: groupsOf(body).map(group => {
			const out = { id: group.id, title: (group.title || '').trim() };
			const description = (group.description || '').trim();
			if (description) out.description = description;
			Object.assign(out, collapsedFields(groupCollapsed(group)));
			// An entry keeps what it asks of the grid (its width and new-row
			// flag) and is written in the API's one spelling — the bare name
			// when it asks for nothing (limits.makeEntry) — so a layout read
			// back and sent again is the layout that was stored. Names are what
			// the filtering and the de-duplication go by.
			out.elements = entriesOf(group).flatMap(raw => {
				const { element: name, span, newRow } = entryOf(raw);
				if (typeof name !== 'string' || seen.has(name)) return [];
				if (known && !known.has(name)) return [];
				seen.add(name);
				return [makeEntry(name, { span, newRow })];
			});
			return out;
		}),
	};
	// What the card leaves out (KPR-1211): known names, once, never one a
	// group places (a placement wins), and the key absent when there is none —
	// the API's one spelling.
	const leftOut = leftOutList(body).filter(name => {
		if (seen.has(name) || (known && !known.has(name))) return false;
		seen.add(name);
		return true;
	});
	if (leftOut.length) out.leftOut = leftOut;
	return out;
}

/** Structural equality of two normalized bodies. */
export function sameFormLayoutBody(a, b) {
	return JSON.stringify(normalizeFormLayoutBody(a)) === JSON.stringify(normalizeFormLayoutBody(b));
}

/** Every name a body places (an entry is a name or `{ element, … }`). Left-out names are not placed. */
export function placedFormNames(body) {
	return new Set(groupsOf(body).flatMap(groupNames));
}

/**
 * What stops the editor from saving, or null: the API's own refusals said
 * before the round trip (a title, a group id, the group cap, and — when the
 * caller passes `defs`, a Map of element name to element — a width the
 * element's type cannot hold: the API refuses a layout that holds one as
 * `span_out_of_range`, so the editor flags it and holds Save with the reason
 * instead of failing after the trip. The CARD's own save is never involved).
 */
export function formLayoutProblem(body, defs = null) {
	const groups = groupsOf(body);
	if (!groups.length) return 'Add a group, or reset the layout to go back to automatic.';
	if (groups.length > MAX_FORM_GROUPS) return `A form layout holds at most ${MAX_FORM_GROUPS} groups.`;
	const ids = new Set();
	for (const group of groups) {
		const title = (group?.title || '').trim();
		if (!title) return 'Every group needs a title.';
		if (title.length > MAX_GROUP_TITLE) return `Group titles are at most ${MAX_GROUP_TITLE} characters.`;
		if ((group?.description || '').trim().length > MAX_GROUP_DESCRIPTION) return `Descriptions are at most ${MAX_GROUP_DESCRIPTION} characters.`;
		// The API reserves the Other fields panel's key (reserved_group_id);
		// newGroupId can never mint it, so only a hand-edited row carries it.
		if (group?.id === OTHER_FIELDS_KEY) return `A group cannot use the id "${OTHER_FIELDS_KEY}" — remove it and add it again.`;
		if (!GROUP_ID.test(group?.id || '') || ids.has(group.id)) return 'Two groups share an id — remove one and add it again.';
		ids.add(group.id);
	}
	const stranded = defs ? strandedSpans(body, defs) : [];
	if (stranded.length) {
		const more = stranded.length > 1 ? ` (and ${stranded.length - 1} more)` : '';
		return `Fix the width of ${stranded[0].label}${more}: a layout cannot be saved with a width its field cannot hold.`;
	}
	return null;
}

/**
 * The placed elements whose stored `span` is outside what their data type can
 * hold: `[{ name, label, span, effective, reason, min, max }]` in layout
 * order — the API's own test (`span_out_of_range`), which holds an element to
 * its own type's limits. `defs` is a Map of element name to element (a name it
 * does not know is skipped: there is nothing to hold it to).
 */
export function strandedSpans(body, defs) {
	const out = [];
	for (const group of groupsOf(body)) {
		for (const raw of entriesOf(group)) {
			const { element: name, span } = entryOf(raw);
			const element = name ? defs.get(name) : null;
			if (!element || span === 'auto') continue;
			const verdict = spanVerdict(element, span);
			if (!verdict.ok) {
				out.push({
					name,
					label: element?.label?.singular || name,
					span,
					effective: verdict.effective,
					reason: verdict.reason,
					min: verdict.min,
					max: verdict.max,
				});
			}
		}
	}
	return out;
}

// ---- editing (pure; every function returns a new body) --------------------

const withGroups = (body, groups) => ({ ...(body || {}), groups });

/**
 * Moves `name` into group `groupId` at `index` (the end when omitted) —
 * from another group, from within the same one, or from nowhere (unplaced).
 * `groupId` null UNPLACES it, which sends it to "Other fields".
 */
export function moveFormElement(body, name, groupId, index = null) {
	// The entry moves, not the name: its width and new-row flag go with it
	// (an element placed from nowhere starts as the bare name, auto). Moving
	// to a group that does not exist leaves the body as it was. A left-out
	// element that is placed, or unplaced, is no longer left out.
	const holder = groupsOf(body).find(group => entriesOf(group).some(raw => entryOf(raw).element === name));
	const carried = holder ? entriesOf(holder).find(raw => entryOf(raw).element === name) : name;
	const groups = groupsOf(body).map(group => ({ ...group, elements: entriesOf(group).filter(raw => entryOf(raw).element !== name) }));
	if (groupId === null || groupId === undefined) return withLeftOut(withGroups(body, groups), leftOutList(body).filter(n => n !== name));
	const target = groups.find(group => group.id === groupId);
	if (!target) return withGroups(body, groupsOf(body));
	const at = index === null || index === undefined ? target.elements.length : Math.max(0, Math.min(index, target.elements.length));
	target.elements = [...target.elements.slice(0, at), carried, ...target.elements.slice(at)];
	return withLeftOut(withGroups(body, groups), leftOutList(body).filter(n => n !== name));
}

/** `body` with `leftOut` set to `names`, the key dropped when there are none. */
function withLeftOut(body, names) {
	const { leftOut, ...rest } = body || {};
	return names.length ? { ...rest, leftOut: names } : rest;
}

/**
 * Leaves `name` out of the form (KPR-1211): out of any group (its width goes
 * with it, as unplacing does) and onto the end of `leftOut`. Whether it MAY be
 * left out is the caller's to ask first (`mayLeaveOut`, and inherited).
 */
export function leaveOutFormElement(body, name) {
	const unplaced = moveFormElement(body, name, null);
	return withLeftOut(unplaced, [...leftOutList(unplaced), name]);
}

/** Puts a left-out element back on the form, unplaced ("Other fields"). */
export function putBackFormElement(body, name) {
	return moveFormElement(body, name, null);
}

/** One step up or down within its group; across a group edge it joins the neighbour. */
export function nudgeFormElement(body, name, direction) {
	const groups = groupsOf(body);
	const groupIndex = groups.findIndex(group => groupNames(group).includes(name));
	if (groupIndex === -1) return body;
	const index = groupNames(groups[groupIndex]).indexOf(name);
	const next = index + direction;
	if (next >= 0 && next < entriesOf(groups[groupIndex]).length) {
		return moveFormElement(body, name, groups[groupIndex].id, next);
	}
	const neighbour = groups[groupIndex + direction];
	if (!neighbour) return body;
	return moveFormElement(body, name, neighbour.id, direction < 0 ? entriesOf(neighbour).length : 0);
}

/**
 * Sets what the layout asks of one PLACED element — `{ span, newRow }`, either
 * or both; what is not named stays as it was — keeping its place. `span`
 * 'auto' (or null) takes it back to its type's Auto width. An element the body
 * does not place is left alone: a width is a property of a placement, and an
 * unplaced element is drawn at Auto in "Other fields". Written in the API's
 * one spelling (makeEntry), so Auto with no new row is the bare name again.
 */
export function setFormElementPlacement(body, name, patch = {}) {
	if (!placementOf(body, name)) return body;
	let done = false;
	return withGroups(body, groupsOf(body).map(group => ({
		...group,
		elements: entriesOf(group).map(raw => {
			const entry = entryOf(raw);
			if (done || entry.element !== name) return raw;
			done = true;
			return makeEntry(name, {
				span: 'span' in patch ? patch.span : entry.span,
				newRow: 'newRow' in patch ? patch.newRow : entry.newRow,
			});
		}),
	})));
}

export function addFormGroup(body, title = 'New group') {
	const groups = groupsOf(body);
	return withGroups(body, [...groups, { id: newGroupId(title, groups), title, elements: [] }]);
}

/** Removes a group; its elements become unplaced ("Other fields"), never lost. */
export function removeFormGroup(body, groupId) {
	return withGroups(body, groupsOf(body).filter(group => group.id !== groupId));
}

export function updateFormGroup(body, groupId, patch) {
	return withGroups(body, groupsOf(body).map(group => (group.id === groupId ? { ...group, ...patch } : group)));
}

/** Sets whether a group starts collapsed on ONE surface ('form' | 'view'), leaving the other as it was. */
export function setFormGroupCollapsed(body, groupId, surface, value) {
	return withGroups(body, groupsOf(body).map(group => {
		if (group.id !== groupId) return group;
		const next = { ...groupCollapsed(group), [surface === VIEW_SURFACE ? 'view' : 'form']: value === true };
		return { ...withoutCollapsed(group), ...collapsedFields(next) };
	}));
}

export function moveFormGroup(body, groupId, direction) {
	const groups = groupsOf(body).slice();
	const index = groups.findIndex(group => group.id === groupId);
	const next = index + direction;
	if (index === -1 || next < 0 || next >= groups.length) return body;
	[groups[index], groups[next]] = [groups[next], groups[index]];
	return withGroups(body, groups);
}
